import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Award } from "lucide-react";

import { sportsService, type Achievement, type Competition, type StudentOption, type Team } from "../../services/sportsService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ACHIEVEMENT_TYPES, achievementConfig, day, labelFrom } from "./sportsLogic";

function AchievementForm({ learners, teams, competitions, onClose, onDone }: { learners: StudentOption[]; teams: Team[]; competitions: Competition[]; onClose: () => void; onDone: (row: Achievement) => void }) {
  const [learner, setLearner] = useState("");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("individual_award");
  const [level, setLevel] = useState("");
  const [team, setTeam] = useState("");
  const [competition, setCompetition] = useState("");
  const [description, setDescription] = useState("");
  const save = useMutation({ mutationFn: sportsService.createAchievement, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      student: Number(learner),
      title: title.trim(),
      achievement_type: kind,
      award_level: level.trim() || null,
      team: team ? Number(team) : null,
      competition: competition ? Number(competition) : null,
      achieved_date: String(data.get("date")),
      certificate_number: String(data.get("certificate") ?? "").trim() || null,
      description: description.trim() || null,
    });
  };
  return (
    <FormModal
      id="achievement"
      title="Record an achievement"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The achievement could not be recorded.") : undefined}
      saveLabel="Record it"
      note="Families see their own child's achievements."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Who" divided={false}>
        <Field label="Learner" as="div" className="sm:col-span-2">
          <SearchPick
            items={learners}
            value={learner}
            onChange={setLearner}
            label={(row) => row.full_name}
            detail={(row) => [row.class_name, row.student_id].filter(Boolean).join(" · ")}
            text={(row) => `${row.full_name} ${row.student_id}`}
            placeholder="Search by name, class or learner number"
            empty="No learner matches"
            required
          />
        </Field>
      </FormSection>
      <FormSection title="What">
        <Field label="Title" className="sm:col-span-2" hint="e.g. Top scorer, Inter-house cup 2026.">
          <input required value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="input mt-1">
            {ACHIEVEMENT_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Level" optional hint="e.g. School, District, National.">
          <input value={level} onChange={(event) => setLevel(event.target.value)} list="award-levels" maxLength={100} className="input mt-1" />
          <datalist id="award-levels">{["School", "Zone", "District", "Regional", "National", "International"].map((item) => <option key={item} value={item} />)}</datalist>
        </Field>
        <Field label="With the team" optional hint="Only a team the learner has played for.">
          <select value={team} onChange={(event) => setTeam(event.target.value)} className="input mt-1">
            <option value="">No team</option>
            {teams.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="At" optional>
          <select value={competition} onChange={(event) => setCompetition(event.target.value)} className="input mt-1">
            <option value="">No competition</option>
            {competitions.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Date">
          <input required type="date" name="date" max={todayIso()} defaultValue={todayIso()} className="input mt-1" />
        </Field>
        <Field label="Certificate number" optional>
          <input name="certificate" maxLength={100} className="input mt-1" />
        </Field>
        <Field label="Citation" optional as="div" className="sm:col-span-2">
          <AIAssistField
            rows={2}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="A line for the certificate or the newsletter."
            context={{ key: "sports.note", params: { kind: "achievement", title, achievement_type: kind, award_level: level, team: team || undefined, competition: competition || undefined } }}
            draftLabel="Help me write it"
            draftHint="Jot what they did; it writes it up without adding anything."
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Awards, records and wins, for the learner's record. */
export function AchievementsPanel({ achievements, learners, teams, competitions, isLoading, isError, onRetry, canManage, isFamily, onChanged }: {
  achievements?: Achievement[];
  learners: StudentOption[];
  teams: Team[];
  competitions: Competition[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  isFamily: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const columns: RegisterColumn<Achievement>[] = [
    { header: "Date", cell: (row) => day(row.achieved_date), exportValue: (row) => day(row.achieved_date), className: "tabular-nums text-gray-700" },
    { header: "Learner", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
    { header: "Achievement", cell: (row) => row.title, exportValue: (row) => row.title, className: "min-w-[14rem] whitespace-normal text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(ACHIEVEMENT_TYPES, row.achievement_type), exportValue: (row) => labelFrom(ACHIEVEMENT_TYPES, row.achievement_type), className: "text-gray-700" },
    { header: "Level", cell: (row) => row.award_level || "—", exportValue: (row) => row.award_level ?? "", className: "text-gray-700" },
    { header: "Team / competition", cell: (row) => [row.team_name, row.competition_name].filter(Boolean).join(" · ") || "—", exportValue: (row) => [row.team_name, row.competition_name].filter(Boolean).join(" · "), className: "text-gray-700" },
  ];
  const item = viewing !== null ? achievements?.find((row) => row.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Achievements"
        description={isFamily ? "Your child's sporting achievements." : "Awards, records and wins."}
        actions={canManage && (
          <button type="button" onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Award className="mr-1.5 h-3.5 w-3.5" /> Record an achievement
          </button>
        )}
        rows={achievements}
        config={achievementConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["achievement", "achievements"]}
        exportName={{ file: "sports-achievements", title: "Sports Achievements" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No achievements recorded yet."
      />
      {item && (
        <DetailsModal
          title={item.title}
          headline={{ label: labelFrom(ACHIEVEMENT_TYPES, item.achievement_type), value: item.student_name, note: [item.award_level, day(item.achieved_date)].filter(Boolean).join(" · ") }}
          facts={[
            ["Team", item.team_name],
            ["Competition", item.competition_name],
            ["Certificate", item.certificate_number ?? undefined],
          ]}
          sections={[{ title: "Citation", body: item.description }]}
          onClose={() => setViewing(null)}
        />
      )}
      {adding && (
        <AchievementForm
          learners={learners}
          teams={teams}
          competitions={competitions}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            setNotice({ tone: "success", text: `“${saved.title}” recorded for ${saved.student_name}.` });
            onChanged();
          }}
        />
      )}
    </div>
  );
}
